import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useLocationsList } from '@/features/locations/api';
import { useDialers } from '@/features/dialers/api';
import {
  useAclModuleList,
  useAclSettingDetail,
} from '../api';
import { AclPermissionMatrix } from '../components/AclPermissionMatrix';
import {
  DIALERS_MODULE_NAME,
  hydratePermissions,
  LOCATIONS_MODULE_NAME,
  NUMBER_ACTION_MODULE_NAME,
  NUMBER_ACTION_OPTIONS,
  USERS_MODULE_NAME,
  parseModuleListingData,
  withParsedChildren,
  type PermissionsMap,
} from '../helpers';

export function ViewAclSettingPage() {
  const { id } = useParams<{ id: string }>();
  const settingId = Number(id);

  const moduleList = useAclModuleList();
  const detail = useAclSettingDetail(settingId);
  const locationsList = useLocationsList();
  const dialersList = useDialers();

  const modules = useMemo(
    () => withParsedChildren(moduleList.data ?? []),
    [moduleList.data],
  );

  const [aclName, setAclName] = useState('');
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [seeded, setSeeded] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [selectedDialerIds, setSelectedDialerIds] = useState<number[]>([]);
  const [selectedUserSettingIds, setSelectedUserSettingIds] = useState<string[]>([]);
  const [selectedNumberActionIds, setSelectedNumberActionIds] = useState<string[]>([]);

  useEffect(() => {
    if (seeded) return;
    if (!modules.length) return;
    if (!detail.data) return;
    setAclName(detail.data.aclName ?? '');
    const { permissions: perms, expanded: exp } = hydratePermissions(
      modules,
      detail.data.moduleOperation,
    );
    setPermissions(perms);
    setExpanded(exp);
    const listing = parseModuleListingData(detail.data.moduleListingData);
    setSelectedLocationIds((listing[LOCATIONS_MODULE_NAME] as number[] | undefined) ?? []);
    setSelectedDialerIds((listing[DIALERS_MODULE_NAME] as number[] | undefined) ?? []);
    setSelectedUserSettingIds(
      (listing[USERS_MODULE_NAME] as string[] | undefined) ?? [],
    );
    setSelectedNumberActionIds(
      (listing[NUMBER_ACTION_MODULE_NAME] as string[] | undefined) ??
        NUMBER_ACTION_OPTIONS.map((o) => o.id),
    );
    setSeeded(true);
  }, [modules, detail.data, seeded]);

  const initialLoading = detail.isLoading || moduleList.isLoading || !seeded;

  if (initialLoading) {
    return (
      <ScrollPage>
        <PageHeader title="View Role" description="Loading…" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-9 w-full max-w-md" />
            <Skeleton className="mt-4 h-40 w-full" />
          </CardContent>
        </Card>
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="View Role"
        description="Read-only view of this role's permissions."
        actions={
          <Button asChild variant="outline">
            <Link to="/acl-settings">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <div className="grid gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Role details</CardTitle>
            <CardDescription>
              This is a read-only view. To make changes, use the Edit page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-1.5 sm:max-w-md">
              <Label htmlFor="aclName">Role name</Label>
              <Input
                id="aclName"
                value={aclName}
                readOnly
                className="cursor-default bg-muted"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Permissions</CardTitle>
            <CardDescription>
              Current permission matrix for this role.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AclPermissionMatrix
              modules={modules}
              permissions={permissions}
              expanded={expanded}
              disabled
              onToggle={() => {}}
              onToggleAll={() => {}}
              onExpand={(rowId, next) =>
                setExpanded((p) => ({ ...p, [rowId]: next }))
              }
              locations={locationsList.data ?? []}
              selectedLocationIds={selectedLocationIds}
              onLocationsChange={() => {}}
              dialers={dialersList.data ?? []}
              selectedDialerIds={selectedDialerIds}
              onDialersChange={() => {}}
              selectedUserSettingIds={selectedUserSettingIds}
              onUserSettingsChange={() => {}}
              selectedNumberActionIds={selectedNumberActionIds}
              onNumberActionChange={() => {}}
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/acl-settings">Back to list</Link>
          </Button>
        </div>
      </div>
    </ScrollPage>
  );
}